import { createAsyncThunk, createAction } from "@reduxjs/toolkit";
import { type AxiosResponse } from "axios";
import { awsClientGateway } from "./common/gateway";
import { logMessage } from "../utils/amplifyLogger";
import { clientLog } from "./logging";
import type { RootState } from "../store/types";
import { getInstanceId } from "./common/serviceUtils";

type RejectFn = (value: string) => unknown;

export const getSettingsData = async (
  instanceId: string,
  rejectWithValue: RejectFn,
): Promise<AxiosResponse | unknown> => {
  try {
    const response = await awsClientGateway({
      pathTemplate: `/userConfig/${instanceId}?apiName=settings`,
      method: "GET",
    });

    return response;
  } catch (error) {
    clientLog({
      component: `settings`,
      message: `Something went wrong in getSettingsData API => ${error}`,
    });
    logMessage(
      `settings API`,
      `Something went wrong in getSettingsData API => ${error}`,
      `error`,
    );
    return rejectWithValue("Error in getSettingsData api endpoint");
  }
};

export const getSettings = createAsyncThunk<
  unknown,
  void,
  { state: RootState }
>("settings/getSettings", async (_args, { getState, rejectWithValue }) => {
  const instanceId = getInstanceId(getState());
  return await getSettingsData(instanceId, rejectWithValue);
});

export const createOrUpdateSettings = createAsyncThunk<
  unknown,
  Record<string, unknown>,
  { state: RootState }
>(
  "settings/createOrUpdateSettings",
  async (settings, { getState, rejectWithValue }) => {
    try {
      const instanceId = getInstanceId(getState());

      await awsClientGateway({
        pathTemplate: `/userConfig/${instanceId}`,
        method: "POST",
        body: { ...settings },
      });
      return await getSettingsData(instanceId, rejectWithValue);
    } catch (error) {
      clientLog({
        component: `settings`,
        message: `Something went wrong in createOrUpdateSettings API => ${error}`,
      });
      logMessage(
        `settings`,
        `Something went wrong in createOrUpdateSettings API => ${error}`,
        `error`,
      );
      return rejectWithValue("Error in createOrUpdateSettings api endpoint");
    }
  },
);

export interface LocaleTranslationsSource {
  languageCode?: string;
  default?: unknown[];
  [key: string]: unknown;
}

export const createOrUpdateLocaleSettings = createAsyncThunk<
  unknown,
  LocaleTranslationsSource | unknown[],
  { state: RootState }
>(
  "settings/createOrUpdateLocaleSettings",
  async (translationsSource, { getState, rejectWithValue }) => {
    const source = translationsSource as LocaleTranslationsSource;
    const isResetToDefalut = source.languageCode ? true : false;
    try {
      const sourceText = isResetToDefalut
        ? (source.default ?? [])
        : translationsSource;
      const bodyData = {
        translations: [...(sourceText as unknown[])],
      };
      const { locale } = await (getState() as { settings: { locale: string } })
        .settings;
      const languageCode = isResetToDefalut ? source.languageCode : locale;
      let response:
        | AxiosResponse
        | { data: { translations: unknown; languageCode: string } };
      if (!languageCode || languageCode === "en") {
        response = {
          data: {
            translations: {
              ...(sourceText as object),
            },
            languageCode: "en",
          },
        };
      } else {
        response = await awsClientGateway({
          pathTemplate: `/translate/${locale}`,
          method: "POST",
          body: { ...bodyData },
        });
      }
      return response;
    } catch (error) {
      clientLog({
        component: `settings`,
        message: `Something went wrong in createOrUpdateLocaleSettings API => ${error}`,
      });
      logMessage(
        `settings`,
        `Something went wrong in createOrUpdateLocaleSettings API => ${error}`,
        `error`,
      );
      return rejectWithValue(
        "Error in createOrUpdateLocaleSettings api endpoint",
      );
    }
  },
);

export interface TabOrderItem {
  tabId: string;
  label: string;
  visible: boolean;
}

export const getReOrderedTablist = createAction<TabOrderItem[] | string>(
  "settings/getReOrderedTablist",
);

export const getLocale = createAction<string>("settings/getLocale");

export const getCCPLocaleStatus = createAction<boolean | string | undefined>(
  "settings/getCCPLocaleStatus",
);

export const getSelectedTimezone = createAction<string>(
  "settings/getSelectedTimezone",
);
export const getUpdatedTimezone = createAction<string>(
  "settings/getUpdatedTimezone",
);
